/* ============================================================================
   CHANVRERY — CHAT « ERDY » : LE RENDU
   ============================================================================

   SOURCE : design/REFERENCE.md (relevé Figma, page 221:6056) et planches.json.
   Planches couvertes ici : C3 · C5 · D1 à D11 · F1 · F2 · G1 à G9 · H1 à H7.

   RÔLE DE CE FICHIER
   Il fabrique du DOM et rien d'autre. Il ne parle JAMAIS au réseau : c'est le
   transport (ch-chat-transport.js) qui appelle les méthodes ci-dessous, et le
   panier (ch-chat-panier.js) qui fabrique les cartes produit.

   ┌─────────────────────────────────────────────────────────────────────────┐
   │ 🔴 LE POINT LE PLUS IMPORTANT DE CE FICHIER : innerHTML EST BANNI.      │
   │                                                                         │
   │ Le texte d'Erdy sort d'un modèle de langage. Un modèle qu'on a réussi   │
   │ à influencer écrit ce qu'on lui a demandé d'écrire. Deux conséquences   │
   │ tenues ici par du code, pas par une consigne de prompt :                │
   │                                                                         │
   │   1. Aucune chaîne venue du serveur n'est jamais assignée à innerHTML   │
   │      ni à insertAdjacentHTML. Tout passe par createTextNode et          │
   │      createElement. Le convertisseur Markdown de la §3 n'accepte que    │
   │      trois formes : gras, puces, liens.                                 │
   │                                                                         │
   │   2. Un lien n'est cliquable que s'il pointe vers le site lui-même ou   │
   │      vers chanvrery.fr. Un lien vers un autre domaine, servi depuis la  │
   │      boutique du marchand et signé de sa mascotte, c'est de             │
   │      l'hameçonnage — avec la caution du marchand. Il est rendu en       │
   │      texte simple, pas en <a>.                                          │
   └─────────────────────────────────────────────────────────────────────────┘

   ⚠️ LES LIBELLÉS. La règle du projet est « aucun texte français en dur dans
   un .js » : la source unique est le bloc <script data-ch-chat-textes> de
   sections/ch-chat-lea.liquid, dont la clé `etats` est aujourd'hui VIDE.
   Trois agents écrivant en parallèle, toucher à la section reviendrait à voir
   la modification écrasée. Les libellés sont donc posés ici en REPLI (§1) et
   fusionnés avec ceux de la section : dès que Julien collera le JSON du
   rapport de livraison dans `"etats"`, le Liquid reprendra la main sans qu'une
   ligne de ce fichier ne change. Rien n'est perdu, rien n'est figé.

   AUCUNE CHIP NE NOMME UN EFFET. Les chips de démarrage viennent du schéma de
   la section (liste fermée de sept libellés validés). Les chips contextuelles
   écrites ici sortent des planches G et H du relevé et ont été relues une par
   une : aucune ne promet un effet — elles parlent d'arômes, d'analyses et de
   catalogue. Toute chip ajoutée passe par la même relecture.
   ========================================================================= */

(function () {
  'use strict';

  /* Le socle définit window.ChanvreryChat. Absent, ce fichier n'a rien à
     piloter : mieux vaut se taire que casser la page de la boutique. */
  var CC = window.ChanvreryChat;
  if (!CC) return;

  /* Évalué deux fois (section dupliquée dans l'éditeur de thème), un seul
     enregistrement suffit — sinon deux modules se disputeraient le même fil. */
  if (CC.module('rendu')) return;

  /* ==========================================================================
     1. LIBELLÉS DE REPLI
     ======================================================================
     Voir l'avertissement du préambule : ceci est un filet, pas la source.
     `textes()` de la section l'emporte, clé par clé, dès qu'elle est remplie.

     Les valeurs des familles G et H sont dérivées de design/planches.json, où
     l'extraction Figma a tronqué la plupart des chaînes à ~32 caractères. Les
     débuts sont recopiés tels quels ; les fins ont été rédigées en français
     sobre, sans allégation d'effet, et sont listées dans le rapport de
     livraison pour relecture juridique.
     ====================================================================== */

  var LIBELLES = {
    /* --- Fil ------------------------------------------------------------ */
    /** D3 — indicateur de frappe. */
    frappe: 'Erdy écrit…',
    /** D1 — signature sous une bulle d'Erdy. Le « IA » redit la planche B3. */
    auteur_lea: 'Erdy · IA',
    /** D6 · D7 · D8 — cycle de vie d'un message client. */
    envoye: 'Envoyé',
    en_cours: 'Envoi en cours…',
    echec: 'Message non envoyé',
    reessayer: 'Réessayer',
    /** D5 — message long replié. */
    voir_plus: 'Voir plus',
    voir_moins: 'Voir moins',
    /** C3 · D10 — séparateurs de jour. */
    aujourdhui: "Aujourd'hui",
    hier: 'Hier',
    /** C5 — remise à zéro. */
    nouvelle_conversation: 'Nouvelle conversation',
    /** B4 — barre de titre du panneau replié. */
    titre_reduit: 'Conversation en cours',
    titre_reduit_un: 'Conversation en cours · 1 non lu',
    titre_reduit_n: 'Conversation en cours · {n} non lus',
    /** G4 — décompte de la planche : « Reprise dans 00:28 ». */
    reprise_dans: 'Reprise dans {mm}:{ss}',
    /** H2 · H6 — libellé du lien repris du pied légal. */
    lire_politique: 'Lire la politique de confidentialité',
    /** H7 — l'effacement est local : il régénère l'identifiant de conversation. */
    supprimer_conversation: 'Supprimer la conversation',

    /* --- Familles G et H ------------------------------------------------ */
    etats: {
      /* G1 — le navigateur a perdu le réseau. Bandeau hors tour. */
      G1_hors_ligne: {
        titre: 'Connexion perdue',
        texte: "Ton message n’est pas parti. Il s’envoie tout seul dès que le réseau revient.",
        note: 'On attend le réseau…',
      },
      /* G2 — incident technique. La cause n'est JAMAIS détaillée : un message
         d'exception nomme le framework et cartographie l'architecture (R5). */
      G2_erreur_serveur: {
        titre: "Erdy n'a pas pu répondre",
        texte: 'Ça a coincé de notre côté, la réponse s’est arrêtée en route. Réessaie dans un instant.',
        note: 'C’est déjà remonté chez nous.',
      },
      /* G3 — budget de temps dépassé côté serveur. */
      G3_delai_depasse: {
        titre: 'Erdy met trop de temps',
        texte: 'Rien n’est arrivé dans les temps. Reformule ta question, ou retente.',
      },
      /* G4 — quota visiteur ou plafond de dépense (M1). `note` est remplacée
         par le décompte quand le serveur renseigne `reprise_dans_s`. */
      G4_trop_de_messages: {
        titre: "Tu vas plus vite qu'Erdy",
        texte: 'Trop de messages en peu de temps. La conversation reprend toute seule.',
      },
      /* G5 — filtrage d'entrée. Le texte dit le périmètre, pas la règle. */
      G5_refus_moderation: {
        titre: 'Là, je ne peux pas t’aider',
        texte: 'Je reste sur le catalogue Chanvrery : les produits, les analyses, les boutiques. Pose-moi une question là-dessus et on avance.',
      },
      /* G6 — recherche catalogue vide. Les alternatives arrivent en cartes
         produit dans le même tour : le texte n'en promet aucune. */
      G6_aucun_produit: {
        titre: 'Rien trouvé au catalogue',
        texte: "Je n'ai pas de référence qui corresponde. Dis-moi un autre profil aromatique, un taux ou un budget.",
      },
      /* G7 — variante en rupture au moment de l'ajout. */
      G7_rupture_stock: {
        titre: 'Ce format est en rupture',
        texte: "Rien n'a été ajouté à ton panier. Je peux te proposer un autre format ou une autre variété.",
      },
      /* G8 — la référence a disparu du catalogue entre-temps. */
      G8_produit_retire: {
        titre: "Cette référence n'est plus au catalogue",
        texte: 'Elle n’est plus chez nous. Je peux te montrer ce qui s’en rapproche dans la même famille.',
      },
      /* G9 — la connexion est revenue, le tour repart. */
      G9_reprise: {
        titre: 'Connexion rétablie',
        texte: 'C’est reparti.',
      },

      /* H1 — la barrière 18+ du thème (ch-overlay-age.liquid) tient déjà la
         vérification bloquante. Ici, c'est un rappel dans le fil. */
      H1_verification_18: {
        titre: 'Tu as 18 ans ou plus ?',
        texte: 'La vente de produits CBD est réservée aux personnes majeures. Tu le confirmes sur l’honneur.',
      },
      /* H2 — R2 et la décision de rétention de la spec §5.
         Texte aligné mot pour mot sur celui du module panier, qui le tient de
         la fiche de cadrage et le marque VERBATIM. C'est lui qui affiche H2 en
         temps normal ; ce repli ne sert que s'il est absent, et deux
         formulations différentes d'une même mention de conformité seraient
         indéfendables en relecture juridique.
         Ce n'est PAS une demande de consentement : la base légale retenue est
         l'intérêt légitime, donc aucun bouton « J'accepte ». */
      H2_consentement_enregistrement: {
        texte:
          'Cette conversation est enregistrée 30 jours pour améliorer le service. ' +
          'Ne partage pas d’informations de santé.',
        politique: true,
      },
      /* H3 — sortie du garde-fou symptôme (R1). La dérivation « Parler arômes »
         est celle du relevé : elle ramène la conversation sur un registre
         aromatique et factuel, le seul autorisé. */
      H3_refus_medical: {
        titre: 'Erdy ne donne pas de conseil médical',
        /* « analyses de lot » retiré ici aussi : même promesse, même défaut.
           Et la chip « Voir les analyses labo » ne menait nulle part — il
           n'existe pas de page qui les rassemble. */
        texte: 'Je ne peux pas te conseiller sur un symptôme ni sur un traitement — ça, c’est pour un professionnel de santé. En revanche, les arômes et les taux mesurés, ça je connais.',
        note: 'Aucune allégation thérapeutique.',
        chips: ['Parler arômes'],
      },
      /* H4 — mention accolée aux recommandations (M5, M6).
       *
       * ⚠️ CE TEXTE A ÉTÉ CORRIGÉ LE 25/08/2026. Il affirmait « analyses de lot
       * consultables sur chaque fiche produit ». C'est FAUX : le relevé des
       * métachamps donne `cbd.analyse_labo` vrai sur 15 produits sur 79, et
       * `legal.coa` vide sur les 79. Le widget promettait un document qui
       * n'existe pas — la faute R4 de l'ancien prestataire, réintroduite dans
       * l'interface.
       *
       * Règle qui en découle : AUCUN texte de ce fichier n'affirme un fait sur
       * les produits, les analyses ou les politiques. Le widget met en forme,
       * le serveur affirme — lui seul a les données sous les yeux.
       *
       * Ce texte n'est plus qu'un REPLI, aligné mot pour mot sur
       * chatbot/src/connaissance/faq-legale.ts (MENTION_RECOMMANDATION). Il ne
       * sert que si le serveur n'envoie rien. */
      H4_mention_thc: {
        texte: 'THC < 0,3 % · Réservé aux 18 ans et plus · Peut rendre un dépistage salivaire positif.',
      },
      /* H5 — le disclaimer permanent vit dans le pied légal du panneau, qui
         n'est jamais masqué. Rien à insérer dans le fil : `null` le dit. */
      H5_disclaimer: null,
      /* H6 — renvoi vers la politique de confidentialité. */
      H6_politique_confidentialite: {
        titre: 'Ce qu’on fait de tes messages',
        texte: 'Tout est écrit noir sur blanc dans notre politique de confidentialité.',
        politique: true,
      },
      /* H7 — droit à l'effacement. Le bouton régénère l'identifiant de
         conversation et vide le fil (C5) : côté serveur, la conversation
         devient orpheline et tombe à l'échéance de rétention. */
      H7_suppression_conversation: {
        titre: 'Supprimer la conversation',
        texte: 'Je peux l’effacer tout de suite. Tes commandes, ton compte et tes e-mails ne sont pas concernés.',
        supprimer: true,
      },
    },
  };

  /** Seuil de repli d'un message long — planche D5 (le texte y fait ~400). */
  var SEUIL_VOIR_PLUS = 400;

  /* Mentions déjà montrées dans cette conversation. Remis à zéro quand le fil
     est vidé — une conversation neuve mérite de revoir la mention légale. */
  var mentionsVues = {};

  /** Tolérance de défilement : au-delà, on considère que le visiteur a remonté. */
  var TOLERANCE_DEFILEMENT = 80;

  /* ==========================================================================
     2. FABRIQUE DOM
     ====================================================================== */

  function elem(nom, classe, texte) {
    var noeud = document.createElement(nom);
    if (classe) noeud.className = classe;
    /* textContent, jamais innerHTML — y compris pour nos propres libellés :
       une seule voie d'écriture, donc une seule à auditer. */
    if (texte != null) noeud.textContent = texte;
    return noeud;
  }

  function vider(noeud) {
    while (noeud.firstChild) noeud.removeChild(noeud.firstChild);
  }

  function retirer(noeud) {
    if (noeud && noeud.parentNode) noeud.parentNode.removeChild(noeud);
  }

  /** Substitution de motifs `{clef}` dans un libellé. */
  function formater(gabarit, valeurs) {
    return String(gabarit).replace(/\{(\w+)\}/g, function (tout, clef) {
      return Object.prototype.hasOwnProperty.call(valeurs, clef) ? String(valeurs[clef]) : tout;
    });
  }

  /**
   * Libellé du fil. La section l'emporte sur le repli, clé par clé.
   * @param {string} clef
   * @returns {string}
   */
  function T(clef) {
    var section = CC.textes() || {};
    return section[clef] != null ? section[clef] : LIBELLES[clef];
  }

  /**
   * Définition d'un état G ou H. Même règle de précédence que T().
   * @param {string} code p. ex. 'H3_refus_medical'
   * @returns {object|null}
   */
  function etatDefini(code) {
    var repli = LIBELLES.etats[code] !== undefined ? LIBELLES.etats[code] : null;

    var section = CC.textes() || {};
    var fourni = section.etats && section.etats[code];
    if (!fourni || typeof fourni !== 'object') return repli;
    if (!repli) return fourni;

    /* Fusion SOUS-CLÉ PAR SOUS-CLÉ, et non remplacement en bloc.
       Le module panier écrit dans le même espace `etats` avec son propre
       vocabulaire de sous-clés (`action`, `rappel`, `confirmer`, `annuler`
       pour H7). Remplacer l'objet entier ferait disparaître en silence les
       sous-clés que lui ne renseigne pas et que ce module attend — un texte
       de conformité qui s'évapore ne fait pas d'erreur, il ne s'affiche
       simplement plus. */
    var fusion = {};
    Object.keys(repli).forEach(function (clef) {
      fusion[clef] = repli[clef];
    });
    Object.keys(fourni).forEach(function (clef) {
      if (fourni[clef] != null && fourni[clef] !== '') fusion[clef] = fourni[clef];
    });
    return fusion;
  }

  /* ==========================================================================
     3. SÉCURITÉ — URL ET MARKDOWN
     ======================================================================
     Tout ce qui suit traite une chaîne écrite par un modèle de langage comme
     une chaîne hostile. Voir l'encadré du préambule.
     ====================================================================== */

  /** Domaines cliquables en plus de l'origine courante. */
  var DOMAINES_AUTORISES = ['chanvrery.fr', 'www.chanvrery.fr'];

  /**
   * Valide une URL écrite par le modèle.
   *
   * ⚠️ La validation porte sur le RÉSULTAT DU PARSING, jamais sur la chaîne
   * brute. C'est ce qui ferme trois pièges classiques d'un test par préfixe :
   *   `//evil.com`            — relatif au protocole, donc un autre domaine
   *   `\\evil.com`            — normalisé en `//evil.com` par le parseur
   *   `https://chanvrery.fr.evil.com` — le préfixe correspond, pas l'hôte
   * On compare donc `hostname` en entier, et on refuse tout ce qui n'est pas
   * http(s) — ce qui écarte `javascript:` et `data:` sans les nommer.
   *
   * @param {string} brut
   * @returns {string} URL absolue sûre, ou '' si elle doit rester du texte.
   */
  function urlSure(brut) {
    var propre = String(brut == null ? '' : brut).trim();
    /* Une cible vide se résoudrait en l'accueil de la boutique : un lien qui
       ne mène nulle part de choisi reste du texte. */
    if (!propre) return '';

    var url;
    try {
      url = new URL(propre, window.location.origin);
    } catch (e) {
      return '';
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';

    var hote = url.hostname.toLowerCase();
    if (hote === window.location.hostname.toLowerCase()) return url.href;
    if (DOMAINES_AUTORISES.indexOf(hote) !== -1) return url.href;

    return '';
  }

  /**
   * Fabrique un lien, ou un simple nœud de texte si l'URL n'est pas sûre.
   * Le libellé reste affiché dans les deux cas : le visiteur voit ce qu'Erdy
   * a écrit, il ne peut simplement pas cliquer vers l'extérieur.
   */
  function lien(url, libelle) {
    var sure = urlSure(url);
    if (!sure) return document.createTextNode(libelle);

    var a = elem('a', 'ch-chat__lien', libelle);
    a.href = sure;
    a.rel = 'noopener';
    return a;
  }

  /* Trois formes acceptées, dans cet ordre de priorité :
       **gras**            gras
       [libellé](url)      lien explicite
       https://…           URL nue, auto-liée par la même validation
     Tout le reste est du texte. Pas d'italique, pas de titre, pas d'image,
     pas de HTML brut : ce qui n'est pas listé ici ne peut pas être produit. */
  var MOTIF_INLINE = /\*\*([^*]+)\*\*|\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>]+)/g;

  /** Une puce : tiret, astérisque ou point médian suivi d'une espace. */
  var MOTIF_PUCE = /^\s*[-*•]\s+/;

  function rendreInline(texte, cible) {
    MOTIF_INLINE.lastIndex = 0;

    var position = 0;
    var trouve;

    while ((trouve = MOTIF_INLINE.exec(texte)) !== null) {
      if (trouve.index > position) {
        cible.appendChild(document.createTextNode(texte.slice(position, trouve.index)));
      }

      if (trouve[1] !== undefined) {
        cible.appendChild(elem('strong', null, trouve[1]));
      } else if (trouve[2] !== undefined) {
        cible.appendChild(lien(trouve[3], trouve[2]));
      } else {
        /* URL nue : la ponctuation finale appartient à la phrase, pas à
           l'adresse. « Voir chanvrery.fr/x. » ne doit pas lier le point. */
        var nue = trouve[4].replace(/[.,;:!?)\]]+$/, '');
        cible.appendChild(lien(nue, nue));
        position = trouve.index + nue.length;
        continue;
      }

      position = MOTIF_INLINE.lastIndex;
    }

    if (position < texte.length) {
      cible.appendChild(document.createTextNode(texte.slice(position)));
    }
  }

  /**
   * Rend un texte Markdown restreint dans un conteneur, qu'il vide d'abord.
   * Planche D2 : gras, puces, un lien. Rien de plus.
   *
   * @param {string} texte
   * @param {HTMLElement} cible
   */
  function rendreMarkdown(texte, cible) {
    vider(cible);

    var lignes = String(texte).split('\n');
    var i = 0;

    while (i < lignes.length) {
      if (!lignes[i].trim()) {
        i++;
        continue;
      }

      if (MOTIF_PUCE.test(lignes[i])) {
        var liste = elem('ul', 'ch-chat__liste');
        while (i < lignes.length && MOTIF_PUCE.test(lignes[i])) {
          var puce = elem('li');
          rendreInline(lignes[i].replace(MOTIF_PUCE, ''), puce);
          liste.appendChild(puce);
          i++;
        }
        cible.appendChild(liste);
        continue;
      }

      /* Lignes consécutives non vides = un paragraphe, sauts internes en <br>.
         Une ligne vide sépare deux paragraphes : c'est la convention Markdown
         et celle qu'écrivent spontanément les modèles. */
      var para = elem('p', 'ch-chat__para');
      var premiere = true;
      while (i < lignes.length && lignes[i].trim() && !MOTIF_PUCE.test(lignes[i])) {
        if (!premiere) para.appendChild(document.createElement('br'));
        rendreInline(lignes[i], para);
        premiere = false;
        i++;
      }
      cible.appendChild(para);
    }
  }

  /**
   * Pendant le streaming, un `**` d'ouverture arrive avant sa fermeture et
   * s'afficherait tel quel pendant quelques centaines de millisecondes. On
   * ferme le gras virtuellement : le texte est déjà en gras quand la vraie
   * fermeture arrive, personne ne voit d'astérisque.
   */
  function equilibrerGras(texte) {
    var paires = texte.match(/\*\*/g);
    return paires && paires.length % 2 === 1 ? texte + '**' : texte;
  }

  /* ==========================================================================
     4. DATES
     ====================================================================== */

  function locale() {
    return (CC.reglages() && CC.reglages().langue) || 'fr';
  }

  /** « 14:32 » — format des planches D1, D6, B2. */
  function heure(date) {
    try {
      return date.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2);
    }
  }

  /** Clé de jour, pour savoir si un séparateur doit être inséré. */
  function clefJour(date) {
    return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
  }

  /** « Aujourd'hui » · « Hier » · « 18 août 2026 » — planches C3 et D10. */
  function libelleJour(date) {
    var maintenant = new Date();
    if (clefJour(date) === clefJour(maintenant)) return T('aujourdhui');

    var veille = new Date(maintenant.getTime() - 86400000);
    if (clefJour(date) === clefJour(veille)) return T('hier');

    try {
      return date.toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return date.toLocaleDateString();
    }
  }

  /* ==========================================================================
     5. ÉTAT DU MODULE
     ====================================================================== */

  var refs = {};
  var pret = false;

  /** Jour du dernier message inséré, pour le séparateur de C3 / D10. */
  var jourAffiche = '';

  /** Non-lus accumulés hors ouverture — planches A2 et B4. */
  var nonLus = 0;

  /** Minuteries de décompte (G4) à interrompre quand le fil est vidé. */
  var minuteries = [];

  function fil() {
    return refs.fil || null;
  }

  function ajouterAuFil(noeud) {
    var conteneur = fil();
    if (!conteneur || !noeud) return null;

    /* ⚠️ MESURÉ AVANT L'INSERTION, et c'est tout l'objet de cette ligne.
       Mesurer après, c'est compter la hauteur du nœud qu'on vient d'ajouter
       dans `scrollHeight` : tout bloc plus haut que la tolérance de 80 px
       faisait alors conclure « le visiteur a remonté le fil » et le défilement
       s'arrêtait. Un bloc de cartes produit fait ~300 px — mesuré au
       navigateur, le fil restait bloqué à 496 px du bas et les deux
       recommandations tombaient hors écran, sans le moindre indice qu'il y
       avait quelque chose dessous. */
    var suivait = estEnBas();
    conteneur.appendChild(noeud);
    defiler(suivait);
    return noeud;
  }

  /** Le visiteur regarde-t-il le bas du fil, à la tolérance près ? */
  function estEnBas() {
    var corps = refs.corps;
    /* Pas encore de corps : rien n'a été lu, donc rien à préserver. */
    if (!corps) return true;
    return corps.scrollHeight - corps.scrollTop - corps.clientHeight <= TOLERANCE_DEFILEMENT;
  }

  /**
   * Défile le corps en bas. Sans `force`, on respecte un visiteur qui a
   * remonté le fil pour relire : rien n'est plus agaçant qu'une lecture
   * arrachée par un message qui arrive.
   */
  function defiler(force) {
    var corps = refs.corps;
    if (!corps) return;

    if (!force && !estEnBas()) return;

    window.requestAnimationFrame(function () {
      corps.scrollTop = corps.scrollHeight;
    });
  }

  /* ==========================================================================
     6. BLOCS
     ====================================================================== */

  /** C3 · C5 · D10 — ligne de séparation datée ou titrée. */
  function separateur(libelle) {
    var bloc = elem('div', 'ch-chat__separateur');
    bloc.setAttribute('role', 'separator');
    bloc.appendChild(elem('span', 'ch-chat__separateur-texte', libelle));
    return bloc;
  }

  /** Insère le séparateur de jour si le jour a changé depuis le dernier message. */
  function assurerJour(date) {
    var clef = clefJour(date);
    if (clef === jourAffiche) return;
    jourAffiche = clef;
    ajouterAuFil(separateur(libelleJour(date)));
  }

  /**
   * Squelette d'un message. Le wrapper porte le rôle et l'état, la bulle porte
   * le fond arrondi, la ligne d'horodatage se pose dessous — c'est le
   * vocabulaire de classes de docs/CONTRAT.md §5.
   */
  function squelette(role) {
    var msg = elem('div', 'ch-chat__msg ch-chat__msg--' + role);
    msg.appendChild(elem('div', 'ch-chat__bulle'));
    msg.appendChild(elem('p', 'ch-chat__horodatage'));
    return msg;
  }

  function bulleDe(msg) {
    return msg.querySelector('.ch-chat__bulle');
  }

  function horodatageDe(msg) {
    return msg.querySelector('.ch-chat__horodatage');
  }

  /** D3 — trois points animés, purement décoratifs. */
  function indicateurFrappe() {
    var bloc = elem('div', 'ch-chat__frappe');
    bloc.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 3; i++) bloc.appendChild(elem('span', 'ch-chat__frappe-point'));
    return bloc;
  }

  /**
   * Libellé d'attente de l'événement SSE `outil`.
   *
   * 🔴 Le `libelle` est déjà rédigé par le serveur (« Je regarde le
   * catalogue… »). Aucun nom d'outil ne sort jamais du serveur (R5) : on
   * l'affiche tel quel, on ne le mappe vers rien, on n'en déduit rien.
   */
  function blocOutil(libelle) {
    var bloc = elem('div', 'ch-chat__outil');
    bloc.appendChild(elem('span', 'ch-chat__outil-texte', libelle));
    return bloc;
  }

  /**
   * Chips contextuelles — planche F2. Le libellé est envoyé tel quel comme
   * message : c'est le même chemin qu'une saisie au clavier, donc les mêmes
   * garde-fous serveur s'appliquent.
   */
  function blocChips(libelles) {
    var bloc = elem('div', 'ch-chat__chips ch-chat__chips--contexte');
    libelles.forEach(function (libelle) {
      var chip = elem('button', 'ch-chat__chip', libelle);
      chip.type = 'button';
      chip.setAttribute('data-ch-chat-chip', libelle);
      chip.setAttribute('aria-pressed', 'false');
      bloc.appendChild(chip);
    });
    return bloc;
  }

  /** Lien vers la politique de confidentialité, repris du pied légal. */
  function lienPolitique(libelle) {
    /* Le pied légal porte déjà l'adresse choisie par le marchand dans le
       schéma de la section (ou celle de la boutique par défaut). La relire
       évite d'inventer un second réglage qui pourrait diverger.

       ⚠️ ON LIT UN ATTRIBUT, PLUS LE DERNIER <a> DE LA LIGNE. Les deux liens
       visibles du pied ont été retirés le 08/09 : la lecture par
       `querySelectorAll('a[href]')` ne trouvait plus rien et renvoyait `null`,
       ce qui faisait disparaître le lien de CETTE mention-ci en silence.
       La source est désormais `data-url-confidentialite`, posé par le gabarit,
       donc indépendante de ce que la ligne affiche. */
    var url = refs.legal ? refs.legal.getAttribute('data-url-confidentialite') : '';
    if (!url) return null;

    /* `lien` est la sous-clé qu'emploie le module panier pour ce libellé :
       on la respecte quand la section la fournit. */
    var a = elem('a', 'ch-chat__lien', libelle || T('lire_politique'));
    a.href = url;
    return a;
  }

  /** Code court d'un état : 'H3_refus_medical' → 'h3'. Sert au modificateur CSS. */
  function codeCourt(code) {
    return String(code).slice(0, 2).toLowerCase();
  }

  /**
   * Construit le bloc d'un état G (bandeau d'erreur) ou H (mention de
   * conformité). Même fabrique, deux classes de base : le contenu a la même
   * forme, seule la portée change.
   *
   * @param {string} code p. ex. 'G4_trop_de_messages'
   * @param {object} [options] { repriseDansS }
   * @returns {HTMLElement|null} null quand l'état ne s'affiche pas (H5).
   */
  function blocEtat(code, options) {
    var definition = etatDefini(code);
    if (!definition) return null;

    var opts = options || {};
    var estErreur = String(code).charAt(0).toUpperCase() === 'G';
    var base = estErreur ? 'ch-chat__etat' : 'ch-chat__mention';
    var court = codeCourt(code);

    /* H2 est posé une seule fois par visiteur, en tête du fil, par le module
       panier (qui tient le témoin en stockage local). Si le serveur renvoie en
       plus l'état sur un `fin`, la mention ne doit pas se dédoubler.
       H4 en revanche accompagne CHAQUE recommandation : pas de déduplication
       générique ici, seul l'état à propriétaire unique est concerné. */
    if (court === 'h2' && fil() && fil().querySelector('.ch-chat__mention--h2')) {
      return null;
    }

    var bloc = elem('div', base + ' ' + base + '--' + court);
    if (estErreur) bloc.setAttribute('role', 'status');

    if (definition.titre) {
      bloc.appendChild(elem('p', base + '-titre', definition.titre));
    }
    if (definition.texte) {
      bloc.appendChild(elem('p', base + '-texte', definition.texte));
    }

    /* G4 — la planche affiche un décompte. Il n'est posé que si le serveur a
       renseigné `reprise_dans_s` : inventer une durée serait mentir. */
    if (typeof opts.repriseDansS === 'number' && opts.repriseDansS > 0) {
      bloc.appendChild(decompte(opts.repriseDansS));
    } else if (definition.note) {
      bloc.appendChild(elem('p', base + '-note', definition.note));
    }

    if (definition.politique) {
      var a = lienPolitique(definition.lien);
      if (a) {
        var ligne = elem('p', base + '-note');
        ligne.appendChild(a);
        bloc.appendChild(ligne);
      }
    }

    /* H7 — droit à l'effacement.
       L'attribut `data-ch-chat-supprimer` est le point de rendez-vous convenu
       par le module panier : il délègue tous les boutons qui le portent vers
       sa carte de confirmation et vers l'effacement réel, quel que soit
       l'agent qui a créé le bouton. On ne réimplémente donc PAS la suppression
       ici — un second chemin divergerait du sien au premier changement. */
    if (definition.supprimer) {
      var bouton = elem('button', 'ch-chat__mention-action', T('supprimer_conversation'));
      bouton.type = 'button';
      bouton.setAttribute('data-ch-chat-supprimer', '');
      bloc.appendChild(bouton);
    }

    if (definition.chips && definition.chips.length) {
      bloc.appendChild(blocChips(definition.chips));
    }

    return bloc;
  }

  /** G4 — « Reprise dans 00:28 », mis à jour chaque seconde. */
  function decompte(secondes) {
    var ligne = elem('p', 'ch-chat__etat-note');
    var restant = Math.ceil(secondes);

    function peindre() {
      var mm = ('0' + Math.floor(restant / 60)).slice(-2);
      var ss = ('0' + (restant % 60)).slice(-2);
      ligne.textContent = formater(T('reprise_dans'), { mm: mm, ss: ss });
    }

    peindre();

    var minuterie = window.setInterval(function () {
      restant -= 1;
      if (restant <= 0) {
        window.clearInterval(minuterie);
        retirer(ligne);
        return;
      }
      peindre();
    }, 1000);

    minuteries.push(minuterie);
    return ligne;
  }

  /**
   * Repli d'un message long — planche D5. On pose un attribut et un bouton ;
   * le clamp visuel appartient à component-ch-chat-etats.css (agent 4), qui
   * lit `[data-tronque]` sur `.ch-chat__msg`.
   */
  function poserVoirPlus(msg, longueur) {
    /* RETIRÉ LE 25/08/2026.
     *
     * La planche D5 prévoyait de replier les réponses longues derrière un
     * « Voir plus ». Dans un vrai fil de discussion, ça n'a pas de sens : on
     * fait défiler. Le bouton coupait la lecture au milieu d'une phrase et
     * laissait un « Voir moins » orphelin sous la signature.
     *
     * La fonction est conservée, inerte : si le repli revient un jour, il
     * suffit de retirer ce `return` — et de commencer par corriger la vraie
     * cause, qui était des réponses trop longues, pas leur affichage. */
    return; // eslint-disable-line no-unreachable
    /* eslint-disable no-unreachable */
    if (longueur < SEUIL_VOIR_PLUS) return;
    if (msg.querySelector('.ch-chat__voir-plus')) return;

    msg.dataset.tronque = 'true';

    var bouton = elem('button', 'ch-chat__voir-plus', T('voir_plus'));
    bouton.type = 'button';
    bouton.setAttribute('aria-expanded', 'false');
    bouton.addEventListener('click', function () {
      var deplie = msg.dataset.tronque !== 'true';
      if (deplie) {
        msg.dataset.tronque = 'true';
        bouton.textContent = T('voir_plus');
        bouton.setAttribute('aria-expanded', 'false');
      } else {
        delete msg.dataset.tronque;
        bouton.textContent = T('voir_moins');
        bouton.setAttribute('aria-expanded', 'true');
        defiler(false);
      }
    });

    /* Après la bulle, avant l'horodatage : le bouton appartient au corps du
       message, pas à sa signature. */
    msg.insertBefore(bouton, horodatageDe(msg));
  }

  /* ==========================================================================
     7. NON-LUS — planches A2 et B4
     ====================================================================== */

  function majTitreReduit() {
    if (!refs.titreReduit) return;

    if (nonLus === 0) {
      refs.titreReduit.textContent = T('titre_reduit');
    } else if (nonLus === 1) {
      refs.titreReduit.textContent = T('titre_reduit_un');
    } else {
      refs.titreReduit.textContent = formater(T('titre_reduit_n'), { n: nonLus });
    }
  }

  /** Une réponse arrivée panneau fermé ou replié compte pour un non-lu. */
  function compterNonLu() {
    if (CC.etat() === CC.ETATS.OUVERT) return;
    nonLus += 1;
    CC.marquerNonLus(nonLus);
    majTitreReduit();
  }

  function remettreNonLus() {
    nonLus = 0;
    CC.marquerNonLus(0);
    majTitreReduit();
  }

  /* ==========================================================================
     8. ACCUEIL ET CHIPS DE DÉMARRAGE — planche F1
     ====================================================================== */

  /**
   * Le message d'accueil n'est posé qu'à la première ouverture, et seulement
   * si le fil est vide : rouvrir le panneau ne doit pas empiler les bonjours.
   */
  function assurerAccueil() {
    var conteneur = fil();
    if (!conteneur) return;

    /* On cherche un MESSAGE, pas un enfant quelconque : le module panier pose
       le bandeau de consentement H2 en tête du fil sur ce même événement
       `ouvert`. Compter les enfants ferait dépendre l'accueil de l'ordre de
       chargement des deux fichiers — il tient aujourd'hui, il ne tiendrait pas
       à la première inversion. */
    if (conteneur.querySelector('.ch-chat__msg')) return;

    var accueil = (CC.textes() || {}).accueil;
    if (!accueil) return;

    api.ajouterMessage({ role: 'lea', texte: accueil });
    montrerChips(true);
  }

  function montrerChips(visible) {
    if (!refs.chips) return;
    refs.chips.hidden = !visible;
  }

  /**
   * Envoie un libellé de chip comme s'il avait été tapé.
   *
   * On repasse par le formulaire du socle plutôt que d'émettre `envoi`
   * directement : le socle tient déjà le verrou d'envoi (F8), la troncature à
   * la limite serveur et la remise à zéro du champ. Dupliquer ces trois règles
   * ici, c'est se condamner à les voir diverger.
   */
  function envoyerLibelle(libelle) {
    var champ = refs.champ;
    var formulaire = refs.formulaire;
    if (!champ || !formulaire) return;

    /* Le champ désactivé est le seul signal de la barrière 18+ : le module
       panier coupe la saisie sans poser `data-saisie`, parce que la cause est
       légale et non réseau (afficher « Saisie indisponible hors ligne » serait
       faux). Sans ce test, un clic sur une chip de démarrage — toujours visible
       sous la barrière — enverrait un message que le clavier, lui, ne peut pas
       envoyer : la vérification de l'âge se contournerait d'un clic. */
    if (champ.disabled) return;

    var racine = CC.racine();
    if (racine && racine.dataset.saisie) return; /* Verrou en cours. */

    champ.value = libelle;
    if (typeof formulaire.requestSubmit === 'function') {
      formulaire.requestSubmit();
    } else {
      formulaire.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /* ==========================================================================
     9. UNE RÉPONSE DE ERDY
     ====================================================================== */

  /**
   * Ouvre une réponse. L'indicateur de frappe (D3) est posé tout de suite : le
   * silence entre l'envoi et le premier fragment est le moment où le visiteur
   * doute que son message soit parti.
   */
  function ouvrirReponse() {
    var conteneur = fil();

    var frappe = indicateurFrappe();
    if (conteneur) ajouterAuFil(frappe);
    CC.annoncer(T('frappe'));

    var msg = null; /* Bulle en cours de rédaction. */
    var bulle = null;
    var outil = null;
    var brut = ''; /* Texte accumulé de la bulle en cours. */
    /* Un tour peut n'avoir produit que des cartes produit : le drapeau compte
       tout contenu inséré, pas seulement du texte. */
    var contenuRendu = false;
    var peinturePrevue = false;
    var clos = false;

    function nettoyerAttente() {
      retirer(frappe);
      retirer(outil);
      outil = null;
    }

    function ouvrirBulle() {
      msg = squelette('lea');

      /* Pendant la rédaction, la bulle sort de la région `aria-live` du fil :
         sans cela, chaque fragment relancerait la lecture depuis le début et
         une réponse de deux cents fragments deviendrait inaudible. Elle y
         rentre d'un coup à `terminer()`. */
      msg.setAttribute('aria-live', 'off');
      msg.setAttribute('aria-busy', 'true');

      bulle = bulleDe(msg);
      brut = '';
      ajouterAuFil(msg);
    }

    /**
     * Le rendu complet est refait à chaque image, pas à chaque fragment : un
     * flux rapide produit des dizaines de fragments par image, et le
     * convertisseur n'a aucune raison de tourner autant de fois.
     */
    function planifierPeinture() {
      if (peinturePrevue || !bulle) return;
      peinturePrevue = true;

      window.requestAnimationFrame(function () {
        peinturePrevue = false;
        if (!bulle) return;

        /* Même raison qu'en §5 : la bulle grandit sur place, donc la mesure
           doit précéder le rendu. Sur une réponse longue, une seule image
           ajoutait plus que la tolérance et le fil décrochait — mesuré à
           256 px du bas en fin de streaming. */
        var suivait = estEnBas();

        rendreMarkdown(equilibrerGras(brut), bulle);

        /* D4 — curseur de streaming, réinséré après chaque rendu. */
        var curseur = elem('span', 'ch-chat__curseur');
        curseur.setAttribute('aria-hidden', 'true');
        bulle.appendChild(curseur);

        defiler(suivait);
      });
    }

    /** Ferme la bulle en cours : le fragment suivant en ouvrira une neuve. */
    function fermerBulle() {
      if (!bulle) return;

      rendreMarkdown(brut, bulle);
      msg.removeAttribute('aria-busy');

      /* Retirer puis réinsérer la bulle vaut une « addition » pour le
         `aria-relevant="additions"` du fil : la réponse est lue une fois,
         complète, au lieu de l'être fragment par fragment. */
      msg.removeAttribute('aria-live');
      msg.insertBefore(bulle, horodatageDe(msg));

      horodatageDe(msg).textContent = T('auteur_lea') + ' · ' + heure(new Date());
      poserVoirPlus(msg, brut.length);

      if (brut.length) contenuRendu = true;
      msg = null;
      bulle = null;
      brut = '';
    }

    return {
      /** D4 — un fragment de texte. */
      delta: function (texte) {
        if (clos || texte == null) return;
        nettoyerAttente();
        if (!bulle) ouvrirBulle();
        brut += texte;
        planifierPeinture();
      },

      /**
       * Libellé d'attente déjà rédigé par le serveur. Il remplace
       * l'indicateur de frappe et disparaît au fragment suivant.
       */
      outil: function (libelle) {
        if (clos || !libelle) return;
        fermerBulle();
        retirer(frappe);

        if (outil) {
          retirer(outil);
        }
        outil = blocOutil(libelle);
        ajouterAuFil(outil);
        CC.annoncer(libelle);
      },

      /**
       * D11 — cartes produit. La fabrication appartient au module panier :
       * lui seul connaît le sélecteur de grammage, l'ajout et l'attribution.
       */
      produits: function (liste) {
        if (clos || !liste || !liste.length) return;
        nettoyerAttente();
        fermerBulle();

        var panier = CC.module('panier');
        var bloc = null;

        if (panier && typeof panier.rendreProduits === 'function') {
          try {
            bloc = panier.rendreProduits(liste);
          } catch (e) {
            console.error('[Chanvrery · chat] rendu des produits en erreur', e);
            bloc = null;
          }
        }

        /* Repli si le module panier manque (asset non déployé, erreur de
           syntaxe) : mieux vaut une liste de noms cliquables que des produits
           trouvés par Erdy puis perdus en route. */
        if (!bloc) bloc = produitsDegrades(liste);

        contenuRendu = true;
        ajouterAuFil(bloc);
        defiler(false);
      },

      /** Fin normale du tour. `etat` porte éventuellement une mention H. */
      terminer: function (etat) {
        if (clos) return;
        clos = true;

        nettoyerAttente();
        fermerBulle();
        CC.annoncer('');

        /* UNE SEULE FOIS PAR CONVERSATION pour les mentions récurrentes.
         *
         * H4 sortait à chaque réponse produit : sur cinq échanges, cinq fois
         * « THC < 0,3 % · Réservé aux 18 ans et plus… ». Le fil devenait
         * illisible, et la mention finissait par ne plus être lue du tout —
         * c'est l'inverse du but recherché.
         *
         * Elle reste OBLIGATOIRE et elle reste affichée : simplement, une fois
         * suffit. Le pied légal du panneau, lui, est permanent et porte déjà
         * « THC < 0,3 % · Vente réservée aux 18 ans et plus ».
         *
         * H3 (refus médical) et la famille G (erreurs) ne sont PAS concernés :
         * chacun répond à un tour précis et doit se répéter s'il se reproduit. */
        if (etat) {
          var recurrent = etat === 'H4_mention_thc';
          if (!recurrent || !mentionsVues[etat]) {
            var mention = blocEtat(etat, null);
            if (mention) {
              ajouterAuFil(mention);
              mentionsVues[etat] = true;
            }
          }
        }

        /* Un tour qui n'a rien produit du tout laisserait le visiteur devant
           un indicateur de frappe qui s'évapore. On le dit. */
        if (!contenuRendu && !etat) {
          var vide = blocEtat('G2_erreur_serveur', null);
          if (vide) ajouterAuFil(vide);
        }

        compterNonLu();
        defiler(false);
      },

      /** Fin en échec — familles G. Un début de réponse déjà reçu est conservé. */
      echouer: function (etat, repriseDansS) {
        if (clos) return;
        clos = true;

        nettoyerAttente();
        fermerBulle();
        CC.annoncer('');

        var bloc = blocEtat(etat || 'G2_erreur_serveur', { repriseDansS: repriseDansS });
        if (bloc) ajouterAuFil(bloc);

        defiler(true);
      },
    };
  }

  /**
   * Repli minimal quand le module panier est absent. Volontairement pauvre :
   * un nom, une provenance, un taux, un lien vers la fiche. Pas de bouton
   * d'ajout — l'ajout au panier et l'attribution du chiffre d'affaires
   * appartiennent au module panier, les refaire ici les ferait diverger.
   */
  function produitsDegrades(liste) {
    var bloc = elem('div', 'ch-chat__produits');

    liste.forEach(function (produit) {
      var carte = elem('div', 'ch-chat__produit');

      var titre = elem('p', 'ch-chat__produit-nom');
      var url = produit.handle ? urlSure('/products/' + produit.handle) : '';
      if (url) {
        var a = elem('a', 'ch-chat__lien', produit.nom);
        a.href = url;
        titre.appendChild(a);
      } else {
        titre.textContent = produit.nom;
      }
      carte.appendChild(titre);

      /* `provenance` et `taux` sont des données contractuelles recopiées de la
         fiche Shopify : affichées telles quelles, jamais reformulées. */
      if (produit.provenance) {
        carte.appendChild(elem('p', 'ch-chat__produit-provenance', produit.provenance));
      }
      if (produit.taux) {
        carte.appendChild(elem('span', 'ch-chat__produit-taux', produit.taux));
      }

      bloc.appendChild(carte);
    });

    return bloc;
  }

  /* ==========================================================================
     10. API DU MODULE
     ====================================================================== */

  var api = {
    /**
     * Insère un message dans le fil.
     *
     * @param {{role:'client'|'lea'|'systeme', texte:string,
     *          horodatage?:string, etat?:'en-cours'|'envoye'|'echec'}} message
     * @returns {HTMLElement|null} le message inséré, à repasser à majMessage.
     */
    ajouterMessage: function (message) {
      if (!fil() || !message) return null;

      var role = message.role === 'client' || message.role === 'systeme' ? message.role : 'lea';
      var date = message.horodatage ? new Date(message.horodatage) : new Date();
      if (isNaN(date.getTime())) date = new Date();

      assurerJour(date);

      var msg = squelette(role);
      /* Le texte du visiteur n'est pas du Markdown : il est rendu littéral.
         Celui d'Erdy passe par le convertisseur restreint de la §3. */
      if (role === 'client') {
        bulleDe(msg).appendChild(elem('p', 'ch-chat__para', message.texte || ''));
      } else {
        rendreMarkdown(message.texte || '', bulleDe(msg));
      }

      msg.dataset.horodatage = date.toISOString();
      ajouterAuFil(msg);

      api.majMessage(msg, message.etat || (role === 'client' ? 'envoye' : null));
      if (role === 'lea') poserVoirPlus(msg, String(message.texte || '').length);

      defiler(role === 'client');
      return msg;
    },

    /**
     * Planches D6, D7, D8 — cycle de vie d'un message client.
     * @param {HTMLElement} el
     * @param {'en-cours'|'envoye'|'echec'} etat
     */
    majMessage: function (el, etat) {
      if (!el) return;

      var ligne = horodatageDe(el);
      var date = el.dataset.horodatage ? new Date(el.dataset.horodatage) : new Date();
      var ancien = el.querySelector('.ch-chat__reessayer');
      if (ancien) retirer(ancien);

      if (!etat) {
        delete el.dataset.etat;
        /* Erdy signe « Erdy · IA · 14:32 » (D1) ; un message système n'a pas
           d'auteur et ne porte que son heure (D9). */
        if (ligne) {
          ligne.textContent = el.classList.contains('ch-chat__msg--lea')
            ? T('auteur_lea') + ' · ' + heure(date)
            : heure(date);
        }
        return;
      }

      el.dataset.etat = etat;

      if (etat === 'en-cours') {
        ligne.textContent = T('en_cours');
        return;
      }

      if (etat === 'envoye') {
        ligne.textContent = T('envoye') + ' · ' + heure(date);
        return;
      }

      /* D8 — échec. Le bouton renvoie le texte d'origine par le chemin
         normal : le socle revalide, le transport rejoue un tour complet. */
      ligne.textContent = T('echec');

      var bouton = elem('button', 'ch-chat__reessayer', T('reessayer'));
      bouton.type = 'button';
      bouton.addEventListener('click', function () {
        var bulle = bulleDe(el);
        var texte = bulle ? bulle.textContent : '';
        retirer(el);
        if (texte) envoyerLibelle(texte);
      });
      el.insertBefore(bouton, ligne);
    },

    ouvrirReponse: ouvrirReponse,

    /**
     * Bandeau d'état hors d'un tour — G1 hors ligne, G9 reprise.
     * Un seul bandeau à la fois : G9 remplace le G1 qu'il annule.
     *
     * @param {string} etat code de EtatErreur ou EtatConformite
     * @param {object} [options] { repriseDansS }
     */
    afficherEtat: function (etat, options) {
      var conteneur = fil();
      if (!conteneur) return null;

      var precedent = conteneur.querySelector('.ch-chat__etat--hors-tour');
      if (precedent) retirer(precedent);

      var bloc = blocEtat(etat, options);
      if (!bloc) return null;

      bloc.classList.add('ch-chat__etat--hors-tour');
      ajouterAuFil(bloc);
      defiler(true);
      return bloc;
    },

    /** Planche C5 — repart de zéro. Appelé par nouvelleConversation(). */
    effacerFil: function () {
      minuteries.forEach(window.clearInterval);
      minuteries = [];

      var conteneur = fil();
      if (conteneur) vider(conteneur);

      jourAffiche = '';
      remettreNonLus();

      /* Une conversation neuve mérite de revoir la mention légale : sans cette
         remise à zéro, un visiteur qui efface son fil ne la reverrait jamais. */
      mentionsVues = {};

      if (!conteneur) return;

      ajouterAuFil(separateur(T('nouvelle_conversation')));
      /* Le fil n'est plus vide après le séparateur : on repose l'accueil à la
         main plutôt que par assurerAccueil(). */
      var accueil = (CC.textes() || {}).accueil;
      if (accueil) api.ajouterMessage({ role: 'lea', texte: accueil });
      montrerChips(true);
      defiler(true);
    },

    /** @param {boolean} [force] défile même si le visiteur a remonté le fil. */
    defilerEnBas: function (force) {
      defiler(Boolean(force));
    },
  };

  /* ==========================================================================
     11. BRANCHEMENTS
     ====================================================================== */

  /** Clic délégué : chips de démarrage, chips contextuelles, action H7. */
  function surClic(evenement) {
    var cible = evenement.target;
    if (!(cible instanceof Element)) return;

    /* Le module panier écoute déjà `[data-ch-chat-supprimer]` et ouvre sa
       carte de confirmation. On ne prend le relais que s'il est absent (asset
       non déployé) : sans quoi un clic ouvrirait la confirmation ET effacerait
       le fil derrière elle. L'effacement de repli reste local — il régénère
       l'identifiant de conversation, et l'échange devenu orphelin tombe à
       l'échéance de rétention. */
    var supprimer = cible.closest('[data-ch-chat-supprimer]');
    if (supprimer) {
      if (CC.module('panier')) return;
      evenement.preventDefault();
      CC.nouvelleConversation();
      return;
    }

    var chip = cible.closest('[data-ch-chat-chip]');
    if (!chip) return;

    evenement.preventDefault();
    chip.setAttribute('aria-pressed', 'true');
    envoyerLibelle(chip.getAttribute('data-ch-chat-chip') || chip.textContent.trim());
  }

  /* `pret` est rémanent : s'y abonner est sûr quel que soit l'ordre de
     chargement des quatre fichiers (cf. préambule de ch-chat.js). */
  CC.ecouter('pret', function (detail) {
    if (pret) return;
    pret = true;

    refs = detail.refs || CC.refs();

    var racine = detail.racine || CC.racine();
    if (racine) racine.addEventListener('click', surClic);

    majTitreReduit();
  });

  /* Le fil ne se remplit qu'à la première ouverture : rien ne sert d'écrire
     dans un panneau `hidden`, et une région `aria-live` remplie hors écran
     bavarde pour rien. */
  CC.ecouter('ouvert', function () {
    assurerAccueil();
    remettreNonLus();
    defiler(true);
  });

  /* F1 — les chips de démarrage disparaissent au premier envoi : ce sont des
     amorces de conversation, pas une barre d'outils permanente. */
  CC.ecouter('envoi', function () {
    montrerChips(false);
  });

  CC.ecouter('conversation-nouvelle', function () {
    /* effacerFil() est déjà appelé par le socle ; on ne fait que rouvrir la
       fenêtre de défilement une fois le nouveau contenu posé. */
    defiler(true);
  });

  CC.enregistrer('rendu', api);
})();
